import { useMemo } from "react";
import { Link, Outlet } from "react-router-dom";

import { useTranslation } from "@/core/i18n";

import { AdminContext, type AdminContextValue } from "@/providers";

import { useAgelessMarketingPlatform } from "./useAgelessMarketingPlatform";

type AgelessMarketingPublicShellProps = {
  readonly platformId: string;
};

/**
 * Chrome for the Ageless Marketing page when nobody is signed in. Deliberately not
 * `AdminLayout`: there is no sidebar, no platform switcher, and no logout — the visitor is
 * the practice's web developer, and the only navigation that makes sense for them is a way
 * to sign in if they turn out to be staff.
 */
export function AgelessMarketingPublicShell({
  platformId,
}: AgelessMarketingPublicShellProps): React.ReactElement {
  const { t } = useTranslation();
  const { name, logoImageUrl } = useAgelessMarketingPlatform(platformId);

  const adminContext = useMemo(
    (): AdminContextValue => ({
      mainContentId: "main-content-wrapper",
      // No session means no platform model and no drawers. `platform` stays undefined rather
      // than being stubbed — components that need platform facts read them from
      // `useAgelessMarketingPlatform`, which has an anonymous source.
      platform: undefined,
      setPlatform: () => {},
      openDrawer: () => {},
      closeDrawer: () => {},
    }),
    [],
  );

  return (
    <AdminContext.Provider value={adminContext}>
      <div className="flex h-full max-h-full flex-col bg-background">
        <header className="z-30 flex h-15 shrink-0 items-center justify-between border-b border-border bg-white px-4">
          <div className="flex items-center gap-3">
            {logoImageUrl ? (
              <img
                alt={name ?? "Practice logo"}
                className="h-8 max-w-[140px] object-contain"
                src={logoImageUrl}
              />
            ) : null}
            {name ? <span className="type-subtitle font-semibold">{name}</span> : null}
          </div>
          <Link className="type-body-sm text-secondary underline" to="/login">
            {t("agelessMarketing.publicShell.signIn")}
          </Link>
        </header>

        <div
          className="relative flex min-h-0 grow flex-col"
          data-testid="main-content-wrapper"
          id="main-content-wrapper"
        >
          <Outlet />
        </div>
      </div>
    </AdminContext.Provider>
  );
}
